import { useQuery } from '@tanstack/react-query';
import {
  getPageTraffic,
  PageTrafficMode,
  PageTrafficResponse,
} from '../../../../../services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../../types/monitor/ChatType';

/** Exported so callers that change the project's own-domain scope (the Domain Share Comparison
 *  drag & drop writes `domainClassification`, which the backend resolves the scope from) can
 *  invalidate this query — the key holds no classification, so it would otherwise stay stale. */
export const GET_PAGE_TRAFFIC = 'page-traffic';

/** The whole ranking for one tab (capped server-side, see PAGE_TRAFFIC_MAX_ROWS), without prompts or
 *  daily trend — prompts load per row through useGetPageTrafficPrompts when the user expands it, and
 *  the table renders no sparkline. */
export const useGetPageTraffic = (
  projectId: number,
  mode: PageTrafficMode,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  monitorPromptIds?: number[],
  filterMonitorTypes?: string[]
) => {
  const queryEnabled = !!projectId && !!chatType && !!from && !!to;

  const fromTimestamp = from?.getTime() ?? null;
  const toTimestamp = to?.getTime() ?? null;

  const { data, isLoading } = useQuery<PageTrafficResponse>({
    queryKey: [
      GET_PAGE_TRAFFIC,
      projectId,
      mode,
      chatType,
      fromTimestamp,
      toTimestamp,
      monitorPromptIds,
      filterMonitorTypes,
    ],
    queryFn: () =>
      getPageTraffic(projectId, mode, chatType!, from, to, monitorPromptIds, filterMonitorTypes),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  return {
    pageTraffic: queryEnabled ? data ?? null : null,
    isLoading: queryEnabled ? isLoading : false,
  };
};
